import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Rehydrate session from localStorage on app load ───────
  useEffect(() => {
    const rehydrate = async () => {
      const token  = localStorage.getItem('accessToken');
      const stored = localStorage.getItem('user');
      if (token && stored) {
        try {
          const parsed = JSON.parse(stored);
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setUser(parsed);

          // Always re-fetch fresh user data (profile_picture, establishments etc)
          try {
            const { data } = await api.get('/auth/me');
            if (data.success) {
              const fresh = { ...parsed, ...data.data };
              localStorage.setItem('user', JSON.stringify(fresh));
              setUser(fresh);
            }
          } catch { /* keep cached user if request fails */ }
        } catch {
          localStorage.clear();
        }
      }
      setLoading(false);
    };
    rehydrate();
  }, []);

  // ── Handle session expiry / idle-timeout events ────────────
  // Uses window.location.replace so no Router context is needed.
  useEffect(() => {
    const handleExpiry = (e) => {
      const isIdle = e?.detail?.reason === 'idle';
      // Read role BEFORE clearing storage — it's gone afterwards
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const dest = (u.role === 'data_provider') ? '/provider-login' : '/login';

      setUser(null);
      localStorage.clear();
      delete api.defaults.headers.common['Authorization'];
      toast.warn(
        isIdle
          ? "You've been logged out due to inactivity."
          : 'Your session has expired. Please log in again.',
        { toastId: 'session-expired' }
      );
      // Small delay so the toast is visible before redirect
      setTimeout(() => window.location.replace(dest), 1500);
    };

    window.addEventListener('session:expired', handleExpiry);
    return () => window.removeEventListener('session:expired', handleExpiry);
  }, []);

  // ── Login ─────────────────────────────────────────────────
  const login = useCallback(async (username, password, isProvider = false) => {
    const endpoint = isProvider ? '/auth/login/provider' : '/auth/login';
    const { data } = await api.post(endpoint, { username, password });

    if (data.success) {
      const { accessToken, refreshToken, user: userData } = data.data;
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;

      // Immediately fetch fresh profile data (profile_picture, sector_type etc)
      let freshUser = userData;
      try {
        const meRes = await api.get('/auth/me');
        if (meRes.data.success) {
          freshUser = { ...userData, ...meRes.data.data };
        }
      } catch { /* use login data as fallback */ }

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(freshUser));
      setUser(freshUser);
      return freshUser;
    }
    throw new Error(data.message);
  }, []);

  // ── Logout ────────────────────────────────────────────────
  // Navigation after logout is handled by the calling component
  // (e.g. InternalLayout calls logout() then navigate('/login'))
  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    try { await api.post('/auth/logout', { refreshToken }); } catch { /* ignore */ }
    localStorage.clear();
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
  }, []);

  const isAdmin        = user?.role === 'admin';
  const isInternal     = user?.role === 'internal' || user?.role === 'admin';
  const isDataProvider = user?.role === 'data_provider';

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, isAdmin, isInternal, isDataProvider }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};